// Pure helpers and shapes for Platform → Batch migration, kept out of the
// components so they can be tested without a DOM. The shapes mirror present()
// in cloud/src/migrations/batch/routes.ts.
import type { MigrationCounts, NotReimported } from "./api"

export type BatchStatus =
  | "scanning" | "ready" | "previewing" | "previewed" | "queued" | "running"
  | "completed" | "failed" | "cancelled"
export type BatchItemStatus = "pending" | "previewed" | "importing" | "completed" | "failed"

export type CompanyTarget = { companyId: string } | { new: { name: string; domain: string } }
export type ProjectTarget = { projectId: string } | { new: { name: string; keyPrefix: string } }
/** A BookStack batch aims each shelf at a Context Hub space instead. */
export type SpaceTarget = { spaceId: string } | { new: { name: string } }

export type BatchSource = "youtrack" | "bookstack"
export const BATCH_SOURCES: Array<{ id: BatchSource; name: string }> = [
  { id: "youtrack", name: "YouTrack" },
  { id: "bookstack", name: "BookStack" },
]

/** A shelf's preview: what it holds, and whether its space can take it. */
export type ShelfPreview = { books: number; pages: number; space: string | null; warnings: string[]; problems: string[] }
export type ShelfSummary = {
  spaceId: string
  spaceName: string
  result: {
    pagesCreated: number
    pagesUpdated: number
    pagesUnchanged: number
    pagesSkipped: number
    pagesRefused: number
    imagesImported: number
    imagesFailed: number
    errors: string[]
    notReimported?: NotReimported[]
    notReimportedOmitted?: number
  }
}

export type BatchItemPreview = {
  counts: MigrationCounts
  board: { statuses: number; types: number; priorities: number; fields: number; labels: number; created: string[] }
  sprintBoards: string[]
  people: { linked: number; created: number; system: number }
  addBytes: number
  keepSourceNumbers: boolean
  warnings: string[]
  problems: string[]
  skipped: number
}
export type BatchItemSummary = {
  counts: MigrationCounts
  warnings: string[]
  newPeople: Array<{ name: string; email: string }>
}

export type BatchItem = {
  id: string
  customer: string
  sourceProject: { id: string; key: string; name: string; issueCount: number }
  boards: Array<{ name: string; sprints: number; shared: boolean }>
  selected: boolean
  companyTarget: CompanyTarget | null
  projectTarget: ProjectTarget | null
  keepSourceNumbers: boolean
  companyId: string | null
  targetProjectId: string | null
  status: BatchItemStatus
  preview: BatchItemPreview | null
  summary: BatchItemSummary | null
  error: string | null
}

export type Batch = {
  id: string
  source: string
  status: BatchStatus
  phase: string | null
  progress: { done: number; total: number; label: string; items?: { done: number; total: number; item: string } } | null
  error: string | null
  credentialHint: string | null
  includeRestricted: boolean
  mappingFile: {
    name: string
    customers: number
    projects: number
    unknownKeys: string[]
    /** BookStack: the mapping the scan proposed, not yet replaced by a file. */
    generated?: boolean
    /** BookStack: books on no shelf, which a batch does not bring. */
    unshelvedBooks?: number
  } | null
  createdByName: string
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  items: BatchItem[]
}

export type TargetCompany = {
  id: string
  name: string
  domain: string
  projects: Array<{ id: string; name: string; keyPrefix: string }>
  /** Its BookStack spaces; filled for a BookStack batch only. */
  spaces?: Array<{ id: string; name: string }>
}

export const UNASSIGNED = "Unassigned"

export const BATCH_STEPS = ["Connect", "Mapping file", "Select", "Preview", "Import", "Report"] as const

/** Where the wizard is, from the batch alone. A batch that has scanned but
 *  has no mapping file yet is still on the upload step. */
export function batchStep(batch: Pick<Batch, "status" | "mappingFile"> | null): number {
  if (!batch) return 1
  switch (batch.status) {
    case "scanning":
      return 2
    case "ready":
      return batch.mappingFile ? 3 : 2
    case "previewing":
    case "previewed":
      return 4
    case "queued":
    case "running":
      return 5
    default:
      return 6
  }
}

/** Statuses where the server is working and the page should keep polling. */
export const isBatchBusy = (status: BatchStatus) =>
  status === "scanning" || status === "previewing" || status === "queued" || status === "running"

export const isBatchFinal = (status: BatchStatus) =>
  status === "completed" || status === "failed" || status === "cancelled"

/** Items grouped by customer, customers in the order their first project
 *  appears, and the Unassigned group always last. */
export function groupByCustomer(items: BatchItem[]): Array<{ customer: string; items: BatchItem[] }> {
  const groups = new Map<string, BatchItem[]>()
  for (const item of items) groups.set(item.customer, [...(groups.get(item.customer) ?? []), item])
  return [...groups]
    .map(([customer, list]) => ({ customer, items: list }))
    .sort((a, z) => Number(a.customer === UNASSIGNED) - Number(z.customer === UNASSIGNED))
}

/** "OLD - MKT - Gonvarri": a project the source has retired by name. */
export const isOld = (item: Pick<BatchItem, "sourceProject">) => /^OLD\s*-/i.test(item.sourceProject.name)

// The same rule as src/projects/projects.ts: 2–10 characters, an uppercase
// letter first, then uppercase letters, digits or _.
export const isValidKey = (value: string) => /^[A-Z][A-Z0-9_]{1,9}$/.test(value)

export const isValidDomain = (value: string) =>
  /^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(value)

/** A selected item that cannot start: not previewed, or with problems. */
export function blockers(items: BatchItem[]): BatchItem[] {
  return items.filter((i) => i.selected && (i.status !== "previewed" || !i.preview || i.preview.problems.length > 0))
}

/** The preview's totals across the selected shelves of a BookStack batch. */
export function shelfTotals(items: BatchItem[]) {
  const selected = items.filter((i) => i.selected && i.preview)
  const preview = (i: BatchItem) => i.preview as unknown as ShelfPreview
  return {
    shelves: selected.length,
    books: selected.reduce((n, i) => n + preview(i).books, 0),
    pages: selected.reduce((n, i) => n + preview(i).pages, 0),
    companies: new Set(selected.map((i) => JSON.stringify(i.companyTarget))).size,
  }
}

/** The preview's totals across the selected projects. */
export function previewTotals(items: BatchItem[]) {
  const selected = items.filter((i) => i.selected && i.preview)
  const sum = (pick: (p: BatchItemPreview) => number) => selected.reduce((n, i) => n + pick(i.preview!), 0)
  return {
    projects: selected.length,
    items: sum((p) => p.counts.items),
    sprints: sum((p) => p.counts.sprints),
    attachmentBytes: sum((p) => p.addBytes),
    peopleLinked: sum((p) => p.people.linked),
    peopleCreated: sum((p) => p.people.created),
    problems: sum((p) => p.problems.length),
  }
}

/** The label of where a customer's projects go, for headers and the report. */
export function companyLabel(target: CompanyTarget | null, companies: Pick<TargetCompany, "id" | "name">[]): string {
  if (!target) return "No company"
  if ("new" in target) return `${target.new.name} (new)`
  return companies.find((c) => c.id === target.companyId)?.name ?? "Existing company"
}

/** The server's suggestKeyPrefix without the collision numbering: uppercase,
 *  leading digits moved to the end ("4SAPHR" → SAPHR4), at most 10. The
 *  server refuses a key already taken in the company. */
export function keyFor(ytKey: string): string {
  let key = ytKey.toUpperCase().replace(/[^A-Z0-9_]/g, "")
  const digits = /^[0-9_]+/.exec(key)?.[0] ?? ""
  key = key.slice(digits.length) + digits.replace(/_/g, "")
  if (!/^[A-Z]/.test(key)) key = `P${key}`
  key = key.slice(0, 10)
  return key.length < 2 ? `${key}X` : key
}
